
// Shared role-related interfaces for both frontend and backend consumers
import { Timestamp } from 'firebase/firestore';

export interface DepartmentRoleAssignment {
  id: string;
  role: string;
  lineOfWork?: boolean;
}

export interface Team {
  id: string;
  code: string;
  displayName: string;
  fullTitle: string;
  description?: string;
  active: boolean;
}

/** Admin user directory row (UserAdminPage / API). */
export interface User {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  position?: string | null;
  generalRole?: string | null;
  departmentId?: string | null;
  departmentIds?: string[];
  teamRoles?: TeamCatalogEntry[];
  disabled?: boolean;
  [key: string]: unknown;
}

/** Partial profile payload for user admin updates. */
export type UserProfile = Partial<
  Pick<User, 'displayName' | 'position' | 'generalRole' | 'departmentIds'>
> & {
  teamRoles?: string[];
};

export interface DirectoryUser {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  createdAt: Timestamp;
  profileComplete: boolean;
  /**
   * @deprecated Replaced by departmentRoles
   */
  department?: string; // This will be the ID of the referenced document

  /**
   * @deprecated Replaced by role in departmentRoles
   */
  roleId?: string; // This will be the ID of the referenced document

  /**
   * @deprecated Legacy team identifiers; prefer departmentRoles
   */
  teamIds?: string[]; // This will be an array of IDs
  lastLoginAt?: Timestamp | null;
  notes?: string;
  phoneNumber?: string | null;
  meta?: any;

  // Resolved roles for permission checking
  /**
   * @deprecated Legacy RBAC role. Prefer departmentRoles/generalRole from AuthContext.
   */
  generalRole?: string | null;

  /**
   * @deprecated Legacy team-level roles (not actively used)
   */
  teamRoles?: TeamRole[];

  departmentRoles?: DepartmentRoleAssignment[];
}

export interface RoleCatalogs {
  departmentRoles: { id: string; name?: string; role?: string; lineOfWork?: boolean }[];
  teamRoles: TeamCatalogEntry[];
}

export const GENERAL_ROLES = ['ADMIN', 'USER', 'VISITOR', 'SUPERVISOR', 'MANAGER', 'TEAM_MEMBER', 'HEAD_WARD_NURSE'] as const;
export type GeneralRole = (typeof GENERAL_ROLES)[number];
export type Role = Lowercase<GeneralRole>;

export const hasRole = (userRole: string | null | undefined, allowedRoles: Role[]): boolean => {
  if (!userRole) return false;
  const normalizedRole = userRole.toLowerCase() as Role;
  return allowedRoles.some((role) => role === normalizedRole);
};

/** Canonical Firestore /team_role doc IDs */
export const TEAM_CATALOG = [
  { id: 'ELD', code: 'ELD', displayName: 'ELD', fullTitle: 'ELD Team', description: 'ELD team', active: true },
  { id: 'ETHIC', code: 'ETHIC', displayName: 'ETHIC', fullTitle: 'ETHIC Team', description: 'ETHIC team', active: true },
  { id: 'IPD_SER', code: 'IPD_SER', displayName: 'IPD SER', fullTitle: 'IPD SER Team', description: 'IPD SER team', active: true },
  { id: 'IT', code: 'IT', displayName: 'IT', fullTitle: 'IT Team', description: 'IT team', active: true },
  { id: 'NN', code: 'NN', displayName: 'NN', fullTitle: 'NN Team', description: 'NN team', active: true },
  { id: 'OCC', code: 'OCC', displayName: 'OCC', fullTitle: 'OCC Team', description: 'OCC team', active: true },
  { id: 'OCC_paricipant', code: 'OCC_paricipant', displayName: 'OCC Participant', fullTitle: 'OCC Participant', description: 'OCC participant', active: true },
  { id: 'OPD_SER', code: 'OPD_SER', displayName: 'OPD SER', fullTitle: 'OPD SER Team', description: 'OPD SER team', active: true },
  { id: 'PPP', code: 'PPP', displayName: 'PPP', fullTitle: 'PPP Team', description: 'PPP team', active: true },
  { id: 'PRODUCT', code: 'PRODUCT', displayName: 'PRODUCT', fullTitle: 'PRODUCT Team', description: 'PRODUCT team', active: true },
  { id: 'RM', code: 'RM', displayName: 'RM', fullTitle: 'RM Team', description: 'RM team', active: true },
  { id: 'RND', code: 'RND', displayName: 'RND', fullTitle: 'RND Team', description: 'RND team', active: true },
  { id: 'SUP', code: 'SUP', displayName: 'SUP', fullTitle: 'SUP Team', description: 'SUP team', active: false },
  { id: 'IC', code: 'IC', displayName: 'IC', fullTitle: 'คณะทำงานเฝ้าระวังและควบคุมการติดเชื้อ', description: 'Infection control team', active: true },
  { id: 'med_error', code: 'med_error', displayName: 'MED ERROR', fullTitle: 'MED ERROR Team', description: 'MED ERROR team', active: true },
] as const satisfies readonly Team[];

export type TeamCatalogEntry = (typeof TEAM_CATALOG)[number];
export type TeamRole = TeamCatalogEntry['code'];

export const TEAM_CATALOG_BY_CODE: Record<TeamRole, TeamCatalogEntry> = TEAM_CATALOG.reduce(
  (acc, team) => {
    acc[team.code] = team;
    return acc;
  },
  {} as Record<TeamRole, TeamCatalogEntry>,
);

export const TEAM_ROLES = TEAM_CATALOG;
export const TEAM_ROLE_CODES = TEAM_CATALOG.map((team) => team.code) as TeamRole[];

export const SN_DEPARTMENT_ROLES = ['ER', 'ICU', 'MED1', 'MED2', 'NSO', 'OBGYN', 'OPD1', 'OPD2', 'HR', 'OR_LOF', 'SPE', 'SUR1', 'SUR2'] as const;

export const SUPERVISOR_DEPARTMENT_ROLES = ['ER', 'ICU', 'MED1', 'MED2', 'NSO', 'OBGYN', 'OPD1', 'OPD2', 'HR', 'OR_LOF', 'SPE', 'SUR1', 'SUR2'] as const;

// Canonical role IDs from /roles collection
export const POSITION_ROLES = ['RN', 'HNURSE', 'DHN', 'SUPERVISOR_NURSE', 'HEAD_WARD_NURSE', 'CN'] as const;
export type PositionRole = (typeof POSITION_ROLES)[number];

export const GENERAL_ROLE_DEFAULTS: Partial<Record<PositionRole | 'SN' | 'HWN', GeneralRole>> = {
  HNURSE: 'ADMIN',
  SUPERVISOR_NURSE: 'SUPERVISOR',
  HEAD_WARD_NURSE: 'HEAD_WARD_NURSE',
  // Legacy aliases (read-only during migration; /roles no longer has SN/HWN docs)
  SN: 'SUPERVISOR',
  HWN: 'HEAD_WARD_NURSE',
};

export type ResourceKey =
  | 'dashboard'
  | 'summary_table'
  | 'compliance'
  | 'admin:roles'
  | 'admin:departments';

export type Permission = 'view' | 'edit' | 'create' | 'delete';
export type PermissionSet = Record<ResourceKey, readonly Permission[]>;

const SUPERVISOR_PERMISSIONS: Partial<PermissionSet> = {
  dashboard: ['view'],
  summary_table: ['view'],
  compliance: ['view'],
};

// Role Definitions
export const CENTRAL_PERMISSION_MAP: Record<string, Partial<PermissionSet>> = {
  // General Roles
  ADMIN: {
    dashboard: ['view', 'edit'],
    summary_table: ['view', 'edit'],
    compliance: ['view', 'edit'],
    'admin:roles': ['view', 'edit'],
    'admin:departments': ['view', 'edit'],
  },
  USER: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view', 'edit'],
  },
  VISITOR: {
    dashboard: ['view'],
    summary_table: ['view'],
  },
  RETIRED: {
    dashboard: ['view'],
    summary_table: ['view'],
  },
  SUPERVISOR: SUPERVISOR_PERMISSIONS,
  SUPERVISOR_NURSE: SUPERVISOR_PERMISSIONS,
  AUDITOR: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view'],
  },
  MANAGER: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view'],
    'admin:roles': ['view'],
    'admin:departments': ['view'],
  },
  TEAM_MEMBER: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view'],
  },
  HEAD_WARD_NURSE: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view', 'edit'],
  },

  // Legacy aliases (for migrated data; /roles no longer has SN/HWN)
  SN: SUPERVISOR_PERMISSIONS,
  HWN: { dashboard: ['view'], summary_table: ['view'], compliance: ['view', 'edit'] },

  // Position-based Roles
  RN: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view', 'edit'],
  },
  HNURSE: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view', 'edit'],
  },
  DHN: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view', 'edit'],
  },
  CN: {
    dashboard: ['view'],
    summary_table: ['view'],
    compliance: ['view'],
  },
};

export interface RoleContext {
  generalRole?: string;
  /** Canonical role IDs (UPPERCASE); permissions = union of all. */
  roles?: string[];
  teamRole?: TeamRole;
  departmentRole?: string;
  positionRole?: string;
  departmentRoles?: (string|DepartmentRoleAssignment)[];
  teamRoles?: string[];
  teamAllowedCategoryIds?: string[];
  primaryTeamId?: string;
  primaryTeamCode?: string;
  teamIds?: string[];
  lineOfWorkDepartmentIds?: string[];
  lofId?: string;
  /** All LOF doc IDs the user has access to (e.g. from lofs.supervisor_nurse reverse lookup). */
  lofIds?: string[];
  allowedCategoryIds?: string[];
  /** Role document id (e.g. "auditor", "supervisor_nurse") when user has a role ref */
  roleId?: string;
  /** TG project page: user has OCC team role (can view management dashboard) */
  hasOCCTeamRole?: boolean;
  /** TG project page: user has OCC_paricipant role (participant; can view own report only) */
  hasOCCParticipantRole?: boolean;
}

/**
 * Supervisor nurse detection for LOF / reporting UI. Align with `hasSupervisorScope` in dashboardData
 * (supervisor tokens only — not head ward nurse).
 * Position role `SN` does not contain the substring `supervisor`.
 */
export function roleIndicatesSupervisorNurse(role: string | undefined | null): boolean {
  if (role == null || typeof role !== 'string') return false;
  const compact = role.trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '');
  if (!compact) return false;
  if (compact === 'sn' || compact === 'sn_supervisor' || compact.endsWith('_sn')) return true;
  return compact.includes('supervisor');
}

// Permission check
export function hasPermission(
  context: RoleContext | string,
  resource: ResourceKey,
  permission: Permission
): boolean {
  if (typeof context === 'string') {
    context = { generalRole: context };
  }

  const roles = [
    ...(context.roles || []),
    context.generalRole,
    context.positionRole,
    ...((context.departmentRoles || []).map(r => typeof r === 'string' ? r : r.role)),
    ...(context.teamRoles || []),
  ].filter((r): r is string => !!r);

  for (const role of roles) {
    const roleId = role.trim().toUpperCase();
    const permissions = CENTRAL_PERMISSION_MAP[roleId]?.[resource];
    if (permissions?.includes(permission)) {
      return true;
    }
  }

  return false;
}

export const normalizeTeamAssignments = (
  teamRoleCodes: (string | TeamRole)[] | undefined,
  teamCatalog: TeamCatalogEntry[],
): TeamCatalogEntry[] => {
  if (!teamRoleCodes || teamRoleCodes.length === 0) {
    return [];
  }

  const normalizedAssignments: TeamCatalogEntry[] = [];
  const teamCatalogMap = new Map<TeamRole, TeamCatalogEntry>(
    teamCatalog.map((team) => [team.code, team]),
  );

  for (const code of teamRoleCodes) {
    if (typeof code === 'string' && teamCatalogMap.has(code as TeamRole)) {
      normalizedAssignments.push(teamCatalogMap.get(code as TeamRole)!);
    }
  }

  return normalizedAssignments;
};
